import axios from "axios";
import toast from "../components/Toast/toast";
import { newAccessToken } from "./apiUrl";

const defaultConfig = {
  baseURL: process.env.REACT_APP_API_BASE_URL,
};

export const API = axios.create(defaultConfig);
export const publicApi = axios.create(defaultConfig);

let isRefreshing = false;
let subscribers = [];

const onRefreshed = (token) => {
  subscribers.forEach((callback) => callback(token));
  subscribers = [];
};

const addSubscriber = (callback) => {
  subscribers.push(callback);
};

// Refresh access token
const getAccessToken = async () => {
  const payload = {
    token: localStorage.getItem("refreshToken"),
  };

  try {
    const { data } = await axios.post(newAccessToken, payload, defaultConfig);
    const accessToken = data?.data?.ACCESS_TOKEN;
    const refreshToken = data?.data?.REFRESH_TOKEN;

    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);

    onRefreshed(accessToken);
    return accessToken;
  } catch (error) {
    console.log("Refresh token failed:", error);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/signin";
    return Promise.reject(error);
  } finally {
    isRefreshing = false;
  }
};

// Attach token to headers
API.interceptors.request.use(
  (config) => {
    //Check internet connection before making any API call
    const isOnline = navigator.onLine;
    if (!isOnline) {
      return Promise.reject({ message: "No internet connection" });
    }

    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle responses and retry on 401
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const { status, data, message } = error.response;

      if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await getAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return API(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return new Promise((resolve) => {
          addSubscriber((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(API(originalRequest));
          });
        });
      }

      // Handle error codes individually
      switch (status) {
        case 400: {
          console.error("Bad Request");
          toast.error(
            data.message
              ? data.message
              : message || "Invalid Value/ Bad Request"
          );
          break;
        }
        case 401: {
          console.warn("Unauthorized - Redirecting to login...");
          toast.error(
            data.message ? data.message : message || "Unauthorized Access"
          );
          break;
        }
        case 403: {
          console.warn("Forbidden - You don’t have permission.");
          toast.error(
            data.message ? data.message : message || "Access Denied/ Forbidden"
          );
          break;
        }
        case 404: {
          console.warn("Not Found - The resource does not exist.");
          toast.error(
            data.message ? data.message : message || "Resource Not Found"
          );
          break;
        }
        case 405: {
          console.warn("Method Not Allowed");
          toast.error(
            data.message ? data.message : message || "Invalid Request"
          );
          break;
        }
        case 422: {
          console.warn("Unprocessable Entity");
          toast.error(
            data.message ? data.message : message || "Already Exists"
          );
          break;
        }
        case 500: {
          console.error("Internal Server Error");
          toast.error(
            data.message ? data.message : message || "Internal Server Error"
          );
          break;
        }
        case 502: {
          console.error("Bad Gateway");
          toast.error(data.message ? data.message : message || "Bad Gateway");
          break;
        }
        case 503: {
          console.error("Service Unavailable");
          toast.error(
            data.message ? data.message : message || "Service is unavailable"
          );
          break;
        }
        case 504: {
          console.error("Gateway Timeout");
          toast.error(data.message ? data.message : message || "Network Error");
          break;
        }
        default: {
          console.error(`Unhandled error: ${status}`);
          toast.error(
            data.message ? data.message : message || "Some Error Occurred"
          );
          break;
        }
      }
    } else if (error.request) {
      console.error("No response received from the server");
      toast.error("No response from server.");
    } else {
      console.error("Request setup error: ", error.message);
      // toast.error("Request error.");
    }

    return Promise.reject(error);
  }
);
